import { useCallback, useEffect, useState } from 'react';

/**
 * Not in the standard DOM lib — Chromium-only, and still non-standard.
 * Declared here so the install flow is typed rather than cast through `any`.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

function isBeforeInstallPrompt(event: Event): event is BeforeInstallPromptEvent {
  return 'prompt' in event && 'userChoice' in event;
}

/** Resolves against the deployed base path, not the domain root. */
export function serviceWorkerUrl(): string {
  return new URL('sw.js', new URL(import.meta.env.BASE_URL, window.location.href)).href;
}

export interface PwaState {
  readonly isOnline: boolean;
  readonly isInstalled: boolean;
  readonly canInstall: boolean;
  readonly updateReady: boolean;
  readonly install: () => Promise<boolean>;
  readonly applyUpdate: () => void;
}

/**
 * Owns service-worker registration and the install prompt.
 *
 * The worker is registered exactly once, from here. Previously `main.jsx` and
 * this hook both registered it, and both used the absolute path `/sw.js` —
 * which 404s on GitHub Pages, where the app is served from
 * `/qr-code-customizer/`. The same mistake made the manifest's `scope`
 * invalid, so the app was never installable in production at all.
 */
export function usePwa(): PwaState {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    const onOnline = (): void => {
      setIsOnline(true);
    };
    const onOffline = (): void => {
      setIsOnline(false);
    };
    const onInstalled = (): void => {
      setIsInstalled(true);
      setInstallEvent(null);
    };
    const onBeforeInstallPrompt = (event: Event): void => {
      if (!isBeforeInstallPrompt(event)) return;
      event.preventDefault();
      setInstallEvent(event);
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    setIsInstalled(window.matchMedia('(display-mode: standalone)').matches);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (import.meta.env.DEV) return;

    let cancelled = false;

    void navigator.serviceWorker
      .register(serviceWorkerUrl(), { scope: import.meta.env.BASE_URL })
      .then((registration) => {
        if (cancelled) return;

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (installing === null) return;

          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
              setWaitingWorker(installing);
            }
          });
        });
      })
      .catch((cause: unknown) => {
        console.error('[PWA] Service Worker konnte nicht registriert werden:', cause);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (installEvent === null) return false;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    setInstallEvent(null);
    return outcome === 'accepted';
  }, [installEvent]);

  const applyUpdate = useCallback(() => {
    if (waitingWorker === null) return;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  }, [waitingWorker]);

  return {
    isOnline,
    isInstalled,
    canInstall: installEvent !== null,
    updateReady: waitingWorker !== null,
    install,
    applyUpdate,
  };
}
